/**
 * Выбор версии Rost — мобильная или полная.
 *
 * ВНИМАНИЕ: у модуля нет ни одного импорта. Сборка (vite.config.ts, плагин
 * `rostPhoneRedirect`) встраивает его код в <head> index.html полной версии: решение
 * «куда открыть» принимается до загрузки девяти мегабайт бандла (research R4).
 * Всё, что здесь нужно, пишется прямо в этом файле, а не подключается.
 */

export type Version = "mobile" | "full";

/** Выбор пользователя на устройстве; `null` — не выбирал */
export type VersionChoice = Version | null;

/** Что нужно знать об устройстве, чтобы понять, телефон ли это */
export interface PhoneEnv {
    /** Результат `matchMedia(query).matches` */
    matches: (query: string) => boolean;
    screenWidth: number;
    screenHeight: number;
}

/** Телефон — устройство, у которого меньшая сторона экрана короче этого значения (CSS px) */
export const PHONE_SHORT_SIDE = 600;

/** Ключ выбора версии (contracts/device-storage.md) */
const CHOICE_KEY = "rost.version";

/**
 * Телефон: указатель — палец без наведения и меньшая сторона экрана меньше 600 CSS px.
 * Планшеты (у них сторона больше) получают полную версию.
 */
export function isPhone(env: PhoneEnv): boolean {
    const touchOnly = env.matches("(hover: none) and (pointer: coarse)");

    return touchOnly && Math.min(env.screenWidth, env.screenHeight) < PHONE_SHORT_SIDE;
}

/** Окружение браузера для `isPhone` */
export function browserEnv(): PhoneEnv {
    return {
        matches: (query) => window.matchMedia(query).matches,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
    };
}

/**
 * Внешние страницы для неавторизованных: регистрация партнёра (`/form`) и
 * регистрация Telegram-бота (`/bot/…`). Они открываются как есть, без перенаправления.
 */
export function isExternalPage(path: string): boolean {
    const pathname = path.split(/[?#]/)[0];

    return pathname === "/form" || pathname.startsWith("/form/") || pathname.startsWith("/bot/");
}

/** Запуск со значка: манифест открывает `/?source=pwa` */
export function isLaunchedFromIcon(search: string): boolean {
    return /[?&]source=pwa(&|$)/.test(search);
}

/**
 * Какую версию открыть (таблица `VersionChoice` в data-model.md):
 * не телефон — полную; телефон со значка — мобильную; телефон с выбором «полная» —
 * полную; иначе мобильную.
 */
export function decideVersion(params: {
    isPhone: boolean;
    choice: VersionChoice;
    launchedFromIcon: boolean;
}): Version {
    if (!params.isPhone) {
        return "full";
    }

    if (params.launchedFromIcon) {
        return "mobile";
    }

    return params.choice === "full" ? "full" : "mobile";
}

/** Выбор версии на устройстве; хранилища нет или значение чужое — `null` */
export function getChoice(): VersionChoice {
    try {
        const value = localStorage.getItem(CHOICE_KEY);

        return value === "mobile" || value === "full" ? value : null;
    } catch {
        return null;
    }
}

/** Запомнить выбор версии на устройстве */
export function setChoice(choice: Version): void {
    try {
        localStorage.setItem(CHOICE_KEY, choice);
    } catch {
        // закрытый режим браузера: выбор просто не запомнится
    }
}

/**
 * Полная версия только для этой вкладки (окна приложения) — `sessionStorage` (FR-005):
 * переход из заглушки раздела, колокольчика, «Нет доступа». Явным выбором это не считается:
 * в новой вкладке и при следующем запуске телефон снова попадёт в мобильную версию.
 */
export function setSessionFull(full: boolean): void {
    try {
        if (full) {
            sessionStorage.setItem(CHOICE_KEY, "full");
        } else {
            sessionStorage.removeItem(CHOICE_KEY);
        }
    } catch {
        // хранилища нет: скрипт перенаправления вернёт телефон в мобильную версию
    }
}

/** Выбор с учётом вкладки: «полная на эту вкладку» сильнее выбора на устройстве */
export function effectiveChoice(): VersionChoice {
    try {
        if (sessionStorage.getItem(CHOICE_KEY) === "full") {
            return "full";
        }
    } catch {
        // нет sessionStorage — остаётся выбор на устройстве
    }

    return getChoice();
}

/**
 * Адрес мобильной версии, на который нужно перенаправить, или `null` — остаться на месте.
 * Именно эту функцию вызывает встроенный в index.html скрипт.
 */
export function redirectTarget(
    location: { pathname: string; search: string; hash?: string },
    env: PhoneEnv,
    choice: VersionChoice
): string | null {
    // якорь тоже уходит в `to`: в нём push о подписи передаёт файл (specs/009 R5)
    const path = location.pathname + location.search + (location.hash ?? "");

    if (location.pathname === "/mobile" || location.pathname.startsWith("/mobile/") || isExternalPage(path)) {
        return null;
    }

    const version = decideVersion({
        isPhone: isPhone(env),
        choice,
        launchedFromIcon: isLaunchedFromIcon(location.search),
    });

    return version === "mobile" ? "/mobile/open?to=" + encodeURIComponent(path) : null;
}
